/**
 * =============================================================================
 * MoveUndoBanner.tsx — BLOCCO 3: Stack multi-livello + Realtime + 7gg history
 * =============================================================================
 *
 * Banner globale per l'undo degli spostamenti pagina.
 *
 * NOVITÀ Blocco 3:
 *   - **Stack multi-livello**: ogni nuovo spostamento si impila in cima allo
 *     stack (LIFO). Il banner mostra l'ultimo elemento + un contatore "+N
 *     altri" se ce ne sono di precedenti ancora annullabili.
 *   - **Finestra estesa a 24h**: lo stack persiste in `localStorage` per 24h
 *     dopo lo spostamento (Q2 — proposta accettata). Oltre le 24h, l'undo
 *     resta possibile fino a 7gg tramite il dialog "Cronologia spostamenti"
 *     che legge da `page_move_history` lato server.
 *   - **Realtime conflict alert**: ascolta gli INSERT su `page_move_history`
 *     e mostra una toast quando un altro utente esegue uno spostamento
 *     (utile per evitare modifiche cieche su strutture condivise).
 *
 * Comportamento esistente preservato:
 *   - Ctrl/Cmd+Z annulla lo spostamento in cima allo stack (se non si sta
 *     scrivendo in un input/textarea/contenteditable).
 *   - Sync tra schede via evento `storage`.
 * =============================================================================
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { undoMovePageToSection, type MoveUndoInfo } from "@/lib/movePageToSection";
import { Button } from "@/components/ui/button";
import { Undo2, X, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";
import { PageMoveHistoryDialog } from "./PageMoveHistoryDialog";

/** Chiave localStorage per lo stack di undo persistente. */
const MOVE_UNDO_STACK_KEY = "move-undo-stack:v2";
/** Chiave LEGACY (Blocco 1): se trovata, viene migrata nello stack. */
const LEGACY_KEY = "move-undo:v1";

/** Finestra di annullamento estesa a 24h (Q2 Blocco 3). */
export const MOVE_UNDO_WINDOW_MS = 24 * 60 * 60 * 1000;

interface PendingUndoEntry {
  undo: MoveUndoInfo;
  expiresAt: number;
  /** Etichetta umana (titolo pagina spostata). */
  label?: string;
  /** Timestamp di creazione, per ordinamento. */
  createdAt: number;
}

// =============================================================================
// API pubblica per chi spinge nuovi undo nello stack.
// =============================================================================

export function setPendingMoveUndo(undo: MoveUndoInfo, label?: string) {
  const entry: PendingUndoEntry = {
    undo,
    label,
    createdAt: Date.now(),
    expiresAt: Date.now() + MOVE_UNDO_WINDOW_MS,
  };
  const stack = readStack();
  // Push in cima (l'ultimo spostamento è il più recente).
  stack.unshift(entry);
  writeStack(stack);
  window.dispatchEvent(new CustomEvent("move-undo:set", { detail: entry }));
}

// =============================================================================
// Helpers di persistenza dello stack.
// =============================================================================

function readStack(): PendingUndoEntry[] {
  try {
    // Migrazione one-shot dalla vecchia chiave Blocco 1 (oggetto singolo).
    const legacyRaw = localStorage.getItem(LEGACY_KEY);
    if (legacyRaw) {
      try {
        const legacy = JSON.parse(legacyRaw);
        if (legacy?.undo && legacy?.expiresAt) {
          const migrated: PendingUndoEntry = {
            undo: legacy.undo,
            label: legacy.label,
            expiresAt: legacy.expiresAt,
            createdAt: legacy.expiresAt - 30_000,
          };
          localStorage.removeItem(LEGACY_KEY);
          const existing = readStackRaw();
          existing.unshift(migrated);
          writeStack(existing);
        } else {
          localStorage.removeItem(LEGACY_KEY);
        }
      } catch {
        localStorage.removeItem(LEGACY_KEY);
      }
    }
    return readStackRaw();
  } catch {
    return [];
  }
}

function readStackRaw(): PendingUndoEntry[] {
  try {
    const raw = localStorage.getItem(MOVE_UNDO_STACK_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as PendingUndoEntry[];
    if (!Array.isArray(arr)) return [];
    const now = Date.now();
    // Filtra le voci scadute.
    return arr.filter((e) => e?.expiresAt && e.expiresAt > now);
  } catch {
    return [];
  }
}

function writeStack(stack: PendingUndoEntry[]) {
  try {
    if (stack.length === 0) {
      localStorage.removeItem(MOVE_UNDO_STACK_KEY);
    } else {
      // Limita a 20 voci max per non gonfiare localStorage.
      localStorage.setItem(MOVE_UNDO_STACK_KEY, JSON.stringify(stack.slice(0, 20)));
    }
  } catch {
    /* noop: localStorage strict */
  }
}

function removeFromStack(historyIdOrToken: string | undefined) {
  if (!historyIdOrToken) return;
  const stack = readStackRaw();
  const next = stack.filter(
    (e) =>
      e.undo.historyId !== historyIdOrToken &&
      e.undo.operationToken !== historyIdOrToken,
  );
  writeStack(next);
  window.dispatchEvent(new Event("move-undo:clear"));
}

// =============================================================================
// Componente Banner
// =============================================================================

export function MoveUndoBanner() {
  const [stack, setStack] = useState<PendingUndoEntry[]>(() => readStack());
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Recupera l'utente corrente per il filtro Realtime (no-self alert).
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setCurrentUserId(data?.user?.id ?? null);
    });
  }, []);

  // Sync stack con eventi/storage.
  useEffect(() => {
    const refresh = () => setStack(readStack());
    window.addEventListener("move-undo:set", refresh);
    window.addEventListener("move-undo:clear", refresh);
    const onStorage = (e: StorageEvent) => {
      if (e.key === MOVE_UNDO_STACK_KEY || e.key === LEGACY_KEY) refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("move-undo:set", refresh);
      window.removeEventListener("move-undo:clear", refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Tick per countdown e auto-purge voci scadute.
  useEffect(() => {
    if (stack.length === 0) return;
    const id = setInterval(() => {
      setNow(Date.now());
      const filtered = readStack();
      if (filtered.length !== stack.length) setStack(filtered);
    }, 1000);
    return () => clearInterval(id);
  }, [stack.length]);

  // Realtime: notifica cross-user quando un altro autore sposta una pagina.
  useEffect(() => {
    if (!currentUserId) return;
    const channel = supabase
      .channel("page-move-history-watcher")
      .on(
        "postgres_changes" as any,
        { event: "INSERT", schema: "public", table: "page_move_history" },
        (payload: any) => {
          const row = payload?.new;
          if (!row || row.user_id === currentUserId) return;
          // Notifica discreta per evitare rumore eccessivo.
          toast.info(
            `Un altro utente ha spostato una pagina: "${row.page_title}"`,
            {
              description: `Da "${row.source_section_title ?? "—"}" a "${row.dest_section_title ?? "—"}"`,
              duration: 8000,
            },
          );
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUserId]);

  // Esegue undo della voce in cima.
  const performUndo = useCallback(
    async (entry: PendingUndoEntry) => {
      if (busy) return;
      setBusy(true);
      try {
        await undoMovePageToSection({ supabase, undo: entry.undo });
        toast.success("Spostamento annullato: pagina ripristinata");
        removeFromStack(entry.undo.historyId ?? entry.undo.operationToken);
        setStack(readStack());
        window.dispatchEvent(new Event("move-undo:applied"));
      } catch (e: any) {
        console.error("Undo persistente fallito:", e);
        toast.error(e?.message || "Impossibile annullare lo spostamento");
      } finally {
        setBusy(false);
      }
    },
    [busy],
  );

  // Scorciatoia tastiera Ctrl/Cmd+Z (annulla la voce in cima).
  useEffect(() => {
    if (stack.length === 0) return;
    const handler = (e: KeyboardEvent) => {
      const isUndoCombo = (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z";
      if (!isUndoCombo) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || target?.isContentEditable) return;
      e.preventDefault();
      void performUndo(stack[0]);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [stack, performUndo]);

  const top = stack[0] ?? null;
  // Countdown: mostra ore/minuti/secondi a seconda di quanto manca.
  const countdownLabel = useMemo(() => {
    if (!top) return "";
    const ms = Math.max(0, top.expiresAt - now);
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    return `${h}h ${min % 60}m`;
  }, [top, now]);

  return (
    <>
      {top && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 rounded-lg border bg-background shadow-lg px-4 py-2 text-sm"
        >
          <Undo2 className="w-4 h-4 text-primary shrink-0" />
          <span className="truncate max-w-[40ch]">
            Pagina spostata{top.label ? `: "${top.label}"` : ""}.
            <span className="text-muted-foreground ml-1">Annullabile per {countdownLabel}</span>
            {stack.length > 1 && (
              <span className="ml-1 inline-flex items-center rounded-full bg-primary/10 text-primary text-xs px-2 py-0.5">
                +{stack.length - 1} altri
              </span>
            )}
          </span>
          <Button size="sm" variant="default" disabled={busy} onClick={() => performUndo(top)}>
            Annulla (Ctrl+Z)
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setHistoryOpen(true)}
            title="Apri cronologia spostamenti recenti"
          >
            <HistoryIcon className="w-4 h-4 mr-1" /> Cronologia
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Chiudi banner"
            onClick={() => {
              // Chiude SOLO il banner attuale: rimuove la voce in cima dallo
              // stack (le altre, se presenti, rimangono annullabili tramite la
              // cronologia o Ctrl+Z).
              removeFromStack(top.undo.historyId ?? top.undo.operationToken);
              setStack(readStack());
            }}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
      <PageMoveHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        onUndoApplied={() => {
          setStack(readStack());
          window.dispatchEvent(new Event("move-undo:applied"));
        }}
      />
    </>
  );
}
